import React from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ApplicationAction } from "../../model_gen/application";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";

interface IntentGroupIntentsTableProps {
  intents: ApplicationAction[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  sortConfig?: SortConfig<ApplicationAction>;
  onSort?: (key: keyof ApplicationAction) => void;
}

const IntentGroupIntentsTable: React.FC<IntentGroupIntentsTableProps> = ({
  intents,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  sortConfig,
  onSort,
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const handleIntentClick = (intent: ApplicationAction) => {
    navigate(`/application/${intent.application_id}/intent/${intent.id}`);
  };

  const handleApplicationClick = (e: React.MouseEvent, applicationId: number) => {
    e.stopPropagation();
    navigate(`/application/${applicationId}`);
  };

  const columns: PaginatedTableColumn<ApplicationAction>[] = [
    {
      key: "meta",
      header: "Application",
      width: "w-[25%]",
      render: (intent) => (
        <button
          onClick={(e) => handleApplicationClick(e, intent.application_id)}
          className="text-sm font-gc-reg text-maitai-green hover:underline"
        >
          {(intent.meta as any)?.metadata?.application_name || "-"}
        </button>
      ),
    },
    {
      key: "action_type",
      header: "Intent",
      width: "w-[25%]",
      sortable: true,
      render: (intent) => (
        <div className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors">
          {intent.action_type}
        </div>
      ),
    },
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[20%]",
      sortable: true,
      render: (intent) => (
        intent.date_created ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(intent.date_created)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(intent.date_created)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        )
      ),
    },
    {
      key: "last_activity",
      header: "Last Activity",
      width: "w-[20%]",
      sortable: true,
      render: (intent) => (
        <div className="text-sm font-gc-reg text-foreground">
          {intent.last_activity && intent.last_activity > 0
            ? moment(intent.last_activity).fromNow()
            : "Never"}
        </div>
      ),
    },
    {
      key: "request_count",
      header: "Requests",
      width: "w-[10%]",
      render: (intent) => (
        <span className="text-sm font-gc-med text-foreground">
          {intent.request_count || 0}
        </span>
      ),
    },
  ];

  return (
    <PaginatedTable<ApplicationAction>
      data={intents}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={itemsPerPage}
      totalItems={totalItems}
      showPagination={true}
      emptyTitle="No intents found"
      emptyDescription="No intents have been created yet."
      onRowClick={handleIntentClick}
      serverSidePagination={true}
    />
  );
};

export default IntentGroupIntentsTable; 